import { Container } from "aurelia-dependency-injection";
import { defineComponent } from "vue";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

import { ToastrService } from "../../../../../../services/toastr.service";
import { PatientService } from "../../../../../../services/patient.service";
import { formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { ImplantableDevice } from "../../../../../../models/pal-health-history/implantable-device";
import { IDeviceData } from "../../../../../../interfaces/i-get-referral";
import { UDITooltip } from "../ImplantableDeviceSection/ImplantableDeviceTooltip";
import { DeviceStatuses } from "../../../../../../enums/ReferralEnums/DeviceStatusEnum";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import ImplantableValidationSection from "../ImplantableValidationSection/ImplantableValidationSection.vue";

export default defineComponent({
    components: {
        FormSection,
        DatePicker,
        ImplantableValidationSection
    },

    props: {
        application: {
            type: Number
        },
        isDeviceValidated: {
            type: Boolean,
            default: false
        },
        isSaveInProgress: {
            type: Boolean,
            default: false
        },
        implantableDevice: {
            type: ImplantableDevice,
            default: null
        }
    },

    validations() {
        return {
            udi: { required }
        };
    },

    data() {
        return {
            deviceStatusEnum: Array.from(DeviceStatuses.enumWithDescriptions.values()),
            _patientsService: null as PatientService,
            _toastrService: null as ToastrService,
            loadingCount: 6,
            udi: "" as string,
            UDITooltip: UDITooltip as string,
            isValidateInProgress: false as boolean,
            v$: useVuelidate(),
            formatDateInput
        };
    },

    emits: ["update:isDeviceValidated"],

    created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._patientsService = Container.instance.get(PatientService);
    },

    watch: {
        udi(newValue, oldValue) {
            this.udiChanged(newValue, oldValue);
        }
    },

    methods: {
        udiChanged(newValue: string, oldValue: string) {
            if (!this.implantableDevice) {
                return;
            }
            if (this.isDeviceValidated && !!oldValue) {
                this.$emit("update:isDeviceValidated", false);
            }
            if (!!newValue) {
                this.implantableDevice.Udi = newValue;
            }
        },

        async validateUdi() {
            try {
                this.isValidateInProgress = true;
                const payload = {
                    udi: this.udi,
                    application: this.application
                };
                let validatedResponse = (await this._patientsService.validateImplantableDevice(payload)) as any;
                if (validatedResponse.isSuccess) {
                    this.assignValidateDataToDevice(validatedResponse.data.DeviceData);
                    this.$emit("update:isDeviceValidated", true);
                    this._toastrService.success({
                        title: `UDI Validated`,
                        message: `The UDI has been successfully validated.`
                    });
                } else {
                    this._toastrService.error({
                        title: `Error - Validate UDI`,
                        message: `${validatedResponse.message}`
                    });
                }
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Error - Validate UDI`,
                    message: `There was a problem while validating the UDI. Please try again.`
                });
            } finally {
                this.isValidateInProgress = false;
            }
        },

        assignValidateDataToDevice(validatedData: IDeviceData) {
            Object.assign(this.implantableDevice, validatedData);
        },

        handleDateChange(date: string) {
            this.implantableDevice.DateOfImplant = date;
        },

        handleDateInputChange(e: any) {
            this.implantableDevice.DateOfImplant = e.target.value;
        }
    }
});
